import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../auth/authStore';
import { makeApi } from '../../lib/api';

/**
 * Phase 5 admin — sparse "AI assist" toolbar that sits above the
 * JSON editor textarea. Each button consumes one of the AI
 * Action endpoints and pipes the result back into the editor.
 *
 * We keep this deliberately minimal — full per-field AI buttons
 * (e.g. a "✨" icon next to each input) need the schema-driven
 * field renderer that's still a 📋 admin-UI item. This toolbar is
 * the end-user-visible proof that the Phase-5 AI endpoints
 * actually work.
 */
export interface AiAssistButtonsProps {
  storyUuid: string | null;
  /** Current draft JSON the editor is showing. */
  currentJson: string;
  /** Called with the new JSON after the AI Action completes. */
  onResult(nextJson: string): void;
}

type Status = { kind: 'idle' } | { kind: 'busy'; what: string } | { kind: 'error'; message: string };

export function AiAssistButtons({
  storyUuid,
  currentJson,
  onResult,
}: AiAssistButtonsProps): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const activeLocale = useAuthStore((s) => s.activeLocale);

  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  // Probe the AI status once per Space so the toolbar can render a
  // configure-your-key hint when ANTHROPIC_API_KEY is unset — much
  // friendlier than letting the user click "Translate" and watch it
  // fail with a 503. Cached 5 min: the env doesn't change live.
  const aiStatusQuery = useQuery({
    enabled: token !== null && spaceSlug !== null,
    queryKey: ['ai-status', spaceSlug],
    queryFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('aiStatus prerequisites missing');
      }
      const result = await makeApi(token).aiStatus(spaceSlug);
      if (result.status !== 'ok') {
        throw new Error(`aiStatus failed: ${result.status}`);
      }
      return result.data.data;
    },
    staleTime: 5 * 60_000,
    retry: 0,
  });

  if (token === null || spaceSlug === null || storyUuid === null) {
    return <></>;
  }

  // No-key fallback: show a single non-blocking hint instead of dead
  // buttons. The query resolves quickly (local config probe — no
  // remote round-trip), so this is the only state the user spends
  // any time in.
  if (aiStatusQuery.data && aiStatusQuery.data.configured === false) {
    return (
      <div
        className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs"
        data-testid="ai-not-configured"
      >
        <span className="font-semibold text-amber-900">✨ AI off</span>
        <span className="text-amber-800">
          {aiStatusQuery.data.configure_hint ??
            'This Peacock instance has no AI provider configured.'}
        </span>
      </div>
    );
  }

  // Returns the parsed draft object, or a typed reason for failure so
  // the UI can show the editor *why* parsing failed (real SyntaxError
  // vs. wrong shape) instead of collapsing both into "JSON must parse".
  function parseDraft():
    | { kind: 'ok'; value: Record<string, unknown> }
    | { kind: 'syntax_error'; message: string }
    | { kind: 'not_object' } {
    try {
      const p: unknown = JSON.parse(currentJson);
      if (typeof p === 'object' && p !== null && !Array.isArray(p)) {
        return { kind: 'ok', value: p as Record<string, unknown> };
      }
      return { kind: 'not_object' };
    } catch (err) {
      return {
        kind: 'syntax_error',
        message: err instanceof Error ? err.message : 'unknown JSON error',
      };
    }
  }

  async function runTranslate(targetLocale: string): Promise<void> {
    const parsed = parseDraft();
    if (parsed.kind !== 'ok') {
      setStatus({
        kind: 'error',
        message:
          parsed.kind === 'syntax_error'
            ? `JSON parse error: ${parsed.message}`
            : 'JSON must be an object, not an array or primitive',
      });
      return;
    }
    const draft = parsed.value;
    setStatus({ kind: 'busy', what: 'Translating' });
    const headline = typeof draft['headline'] === 'string' ? (draft['headline'] as string) : null;
    if (headline === null) {
      setStatus({ kind: 'error', message: 'No `headline` field to translate' });
      return;
    }
    if (token === null || spaceSlug === null) {
      setStatus({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    const api = makeApi(token);
    const result = await api.aiTranslate(spaceSlug, headline, activeLocale, targetLocale);
    if (result.status !== 'ok') {
      setStatus({ kind: 'error', message: `Translate failed: ${result.status}` });
      return;
    }
    const updated = { ...draft, headline: result.data.data.translated };
    onResult(JSON.stringify(updated, null, 2));
    setStatus({ kind: 'idle' });
  }

  async function runSeoMeta(): Promise<void> {
    const parsed = parseDraft();
    if (parsed.kind !== 'ok') {
      setStatus({
        kind: 'error',
        message:
          parsed.kind === 'syntax_error'
            ? `JSON parse error: ${parsed.message}`
            : 'JSON must be an object, not an array or primitive',
      });
      return;
    }
    const draft = parsed.value;
    setStatus({ kind: 'busy', what: 'Generating SEO meta' });
    if (token === null || spaceSlug === null || storyUuid === null) {
      setStatus({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    const api = makeApi(token);
    const result = await api.aiSeoMeta(spaceSlug, storyUuid);
    if (result.status !== 'ok') {
      setStatus({ kind: 'error', message: `SEO meta failed: ${result.status}` });
      return;
    }
    const updated = {
      ...draft,
      meta: {
        ...(typeof draft['meta'] === 'object' && draft['meta'] !== null ? draft['meta'] : {}),
        title: result.data.data.title,
        description: result.data.data.description,
      },
    };
    onResult(JSON.stringify(updated, null, 2));
    setStatus({ kind: 'idle' });
  }

  async function runInternalLinks(): Promise<void> {
    setStatus({ kind: 'busy', what: 'Suggesting internal links' });
    if (token === null || spaceSlug === null || storyUuid === null) {
      setStatus({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    const api = makeApi(token);
    const result = await api.aiInternalLinks(spaceSlug, storyUuid);
    if (result.status !== 'ok') {
      setStatus({ kind: 'error', message: `Internal-link suggestion failed: ${result.status}` });
      return;
    }
    // Internal-link suggestions don't auto-patch — they're suggestions.
    // For Phase 5 we surface them in an alert; future polish renders
    // them as a side-panel the editor can accept individually.
    const summary = result.data.data.suggestions
      .map((s) => `• ${s.anchor} → ${s.target_uuid}\n  ${s.reason}`)
      .join('\n');
    window.alert('Internal link suggestions:\n\n' + (summary || '(no suggestions)'));
    setStatus({ kind: 'idle' });
  }

  const busy = status.kind === 'busy';

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
      <span className="font-semibold text-amber-900">✨ AI</span>

      <button
        type="button"
        disabled={busy}
        onClick={() => void runTranslate('en-GB')}
        className="rounded border border-amber-300 bg-white px-2 py-1 font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Translate headline → en-GB
      </button>

      <button
        type="button"
        disabled={busy}
        onClick={() => void runSeoMeta()}
        className="rounded border border-amber-300 bg-white px-2 py-1 font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Generate SEO meta
      </button>

      <button
        type="button"
        disabled={busy}
        onClick={() => void runInternalLinks()}
        className="rounded border border-amber-300 bg-white px-2 py-1 font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Suggest internal links
      </button>

      {status.kind === 'busy' ? <span className="text-amber-700">{status.what}…</span> : null}
      {status.kind === 'error' ? (
        <span role="alert" className="text-red-700">
          {status.message}
        </span>
      ) : null}
    </div>
  );
}
